import React, { useState } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';

import Tabs from '../../../app/components/Tabs';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { ManualSearch } from '../../components/Manual/ManualSearch';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import {
  manualData,
  validate,
} from '../../../common/spdCore/contexts/ManualContext';
import { ManualEditor } from '../../components/Manual/ManualEditor';
import { useAuth } from '../../../common/hooks/useAuth';
import { Role } from '../../../common/spdCore/authorization/roles';
import { NewFile } from '../../../common/components/File/DragNDropFiles';
import { CreateUpdateManualDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/CreateUpdateManualDto';
import { GetAttachmentDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/GetAttachmentDto';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient/runtime';

export const ManualsView: React.FC = () => {
  const spd = useSpdCore();
  const auth = useAuth();
  const [show, setShow] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [manualId, setManualId] = useState<string>();
  const [manual, setManual] = useState<CreateUpdateManualDto>(manualData());
  const [manualAttachments, setManualAttachments] = useState<
    GetAttachmentDto[]
  >([]);
  const [filesToAdd, setFilesToAdd] = useState<NewFile[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
  const [maxFileSizeExceeded, setMaxFileSizeExceeded] = useState(false);

  const isSuperAdmin = auth?.roles?.includes(Role.SuperAdmin);

  const handleAdd = () => {
    setManualId(undefined);
    setManual(manualData());
    setManualAttachments([]);
    setFilesToAdd([]);
    setFilesToDelete([]);
    setMaxFileSizeExceeded(false);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setManualAttachments([]);
    setFilesToAdd([]);
    setFilesToDelete([]);
    setMaxFileSizeExceeded(false);
    setRefresh(!refresh);
  };

  const saveManual = async (manualCu: CreateUpdateManualDto, id?: string) => {
    if (maxFileSizeExceeded) {
      return {
        saved: false,
        errors: ['Przekroczono dozwolony rozmiar plików.'],
      };
    }

    const status = await validate(manualCu);
    if (!status.valid) {
      return {
        saved: false,
        errors: status.errors,
      };
    }

    try {
      if (id) {
        await spd.manuals.update(id, manualCu);
      } else {
        await spd.manuals.create(manualCu);
      }

      setShow(false);
      setManualAttachments([]);
      setFilesToAdd([]);
      setFilesToDelete([]);
      setMaxFileSizeExceeded(false);
      setRefresh(!refresh);
      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      return {
        saved: false,
        errors: await responseErrors(response as ResponseError),
      };
    }
  };

  const onRowClick = async (id: string) => {
    const getManual = await spd.manuals.get(id);
    setManualId(getManual.id);
    setManual(manualData(getManual));
    setManualAttachments(getManual.attachments || []);
    setFilesToAdd([]);
    setFilesToDelete([]);
    setMaxFileSizeExceeded(false);
    setShow(true);
  };

  return (
    <Card>
      <Tabs defaultActiveKey="search" id="manuals" className="mb-3">
        <Tab eventKey="search" title="Podręczniki" className="m-3">
          <ManualSearch
            getData={spd.manuals.getAllByPortion}
            getCount={spd.manuals.getAllCount}
            refresh={refresh}
            onRowClick={onRowClick}
          />
          {isSuperAdmin && (
            <Button variant="outline-primary" onClick={handleAdd}>
              Nowy podręcznik
            </Button>
          )}
          <ManualEditor
            id={manualId}
            manual={manual}
            onCompleted={saveManual}
            show={show}
            handleClose={handleClose}
            existingAttachments={manualAttachments}
            filesToAdd={filesToAdd}
            filesToDelete={filesToDelete}
            setFilesToAdd={setFilesToAdd}
            setFilesToDelete={setFilesToDelete}
            setMaxFileSizeExceeded={setMaxFileSizeExceeded}
            readOnly={!isSuperAdmin}
          />
        </Tab>
      </Tabs>
    </Card>
  );
};
